import { Form, FormikProvider, useFormik } from "formik";
import { z } from "zod";
import { DocumentCategory } from "@common/types/documentCategory";
import { updateDocumentCategoryBodySchema } from "@common/schemas/documentCategory/document-category.schema";
import { useUpdateDocumentCategoryMutation } from "@api/documentCategory.api";
import { submitHelper, validateFormikWithZodSchema } from "@helpers/form";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { Button } from "@design-system/Button/Button";
import { StandardModalHeader } from "@design-system/Modal/Modal";
import { createFormikFields } from "@components/forms/fields/createFormikFields";
import { HStack } from "@design-system/HStack/HStack";
import { VStack } from "@design-system/VStack/VStack";

type UpdateDocumentCategoryFormSchema = z.infer<
    typeof updateDocumentCategoryBodySchema
>;

const fields = createFormikFields<UpdateDocumentCategoryFormSchema>();

export const UpdateDocumentCategoryModal = ({
    category,
    onHide,
}: {
    category: DocumentCategory;
    onHide: () => void;
}) => {
    const updateMutation = useUpdateDocumentCategoryMutation(category);

    const formik = useFormik<UpdateDocumentCategoryFormSchema>({
        initialValues: {
            label: category.label ?? "",
            description: category.description ?? "",
        },
        validate: validateFormikWithZodSchema(updateDocumentCategoryBodySchema),
        onSubmit: submitHelper({
            loading: updateMutation.isPending,
            handler: async (data) => {
                await updateMutation.mutateAsync({
                    description: data.description ?? null,
                    label: data.label,
                });
                onHide();
            },
        }),
        validateOnMount: true,
    });

    return (
        <>
            <StandardModalHeader>Update subtype</StandardModalHeader>
            <FormikProvider value={formik}>
                <Form onSubmit={formik.handleSubmit}>
                    <VStack gap={16}>
                        <fields.Text name="label" label="Label" size="xl" />
                        <fields.Text
                            name="description"
                            label="Description"
                            optional
                            size="xl"
                        />

                        <HStack as="footer" justify="end" gap={12}>
                            <Button
                                variant="secondary"
                                size="xl"
                                type="button"
                                onClick={onHide}
                                disabled={updateMutation.isPending}
                            >
                                Cancel
                            </Button>

                            <ButtonWithLoader
                                variant="primary"
                                size="xl"
                                loading={updateMutation.isPending}
                                type="submit"
                                disabled={!formik.isValid}
                                hotKey="mod+enter"
                                hotKeyOptions={{
                                    description: "Save subtype",
                                    preventDefault: true,
                                    enableOnFormTags: true,
                                }}
                            >
                                Save changes
                            </ButtonWithLoader>
                        </HStack>
                    </VStack>
                </Form>
            </FormikProvider>
        </>
    );
};
